import { Control, UseFormSetValue } from "react-hook-form";
import {
  ConfigInputTextComponent,
  InputTextPropertyForm,
  InputTextViewComponent,
  PlainInputTextComponent,
} from "./FieldComponent/InputTextComponent";

import makeStyles from "@mui/styles/makeStyles";
import {
  ConfigDatePickerComponent,
  DatePickerPropertyForm,
  DatePickerViewComponent,
  PlainDatePickerComponent,
} from "components/playbook/CustomForm/FieldComponent/DatePickerComponent";
import { Dispatch, SetStateAction } from "react";
import {
  ConfigDropDownComponent,
  DropDownPropertyForm,
  DropDownViewComponent,
  PlainDropDownComponent,
} from "./FieldComponent/DropDownComponent";
import {
  ConfigMarkdownComponent,
  PlainMarkdownComponent,
  MarkdownPropertyForm,
  MarkdownViewComponent,
} from "components/playbook/CustomForm/FieldComponent/MarkdownComponent";

export type FieldPropertyDef = {
  name: string;
  title: string;
  placeholder?: string;
  value: string;
};

export type FieldRuleDef = {
  name: string;
  title: string;
  placeholder?: string;
  value: string;
  message: string;
};

export type FieldOptionDef = {
  optionId: string;
  optionValue: string;
};

export type ConditionRuleDef = {
  ruleId: string;
  fieldId: string;
  fieldValue: FieldOptionDef[];
};

export type FieldState = {
  id: string;
  fieldType: string;
  fieldSubType?: string;
  passValidate: boolean;
  isMultiple?: boolean;
  defaultValue: string | string[];
  options?: FieldOptionDef[];
  label: FieldPropertyDef;
  placeholder: FieldPropertyDef;
  rules: {
    required: FieldRuleDef;
    maxLength: FieldRuleDef;
  };
  // conditions: only support one element
  conditions?: ConditionRuleDef[];
  dependsBy?: string[];
  visible?: boolean;
};

export const useStyles = makeStyles(() => ({
  selectedField: {
    flexBasis: "100%",
    marginTop: 8,
    backgroundColor: "#E6F2FF",
    padding: 5,
    borderRadius: 4,
  },
  unselectedInnerField: {
    flexBasis: "100%",
    marginTop: 8,
    padding: 5,
  },
  unselectedField: {
    flexBasis: "100%",
    marginTop: 8,
  },
  trashIcon: { color: "white", fontSize: "1rem", cursor: "pointer" },
  tooltip: {
    margin: 0,
    background: "rgba(0, 0, 0, 0.5)",
    padding: 4,
    lineHeight: 0,
    minWidth: "unset",
    width: "fit-content",
  },
  cell: {
    display: "inline-flex",
    alignItems: "center",
    width: "32%", //"48%",
    margin: 4,
  },
  field: {
    color: "#898989",
    fontSize: 12,
    minWidth: 90,
    lineHeight: "32px",
  },
  value: {
    fontSize: 12,
    color: "#333",
  },
  labelWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 20,
    borderRadius: 12,
    border: "1px solid #DEDEDE",
    marginLeft: 5,
  },
  txt: {
    fontSize: 11,
  },
  infoRow: {
    display: "flex",
    marginBottom: 10,
    "&:last-of-type": {
      marginBottom: 0,
    },
  },
  name: {
    color: "#007AA3",
    fontSize: 12,
    cursor: "pointer",
  },
}));

export const ComponentHelper = {
  "input-text": {
    fieldComponent: (
      type: string,
      id: string,
      control: Control,
      setValue: UseFormSetValue<any>,
      enableValidate: boolean,
      stateList: FieldState[],
      setStateList: Dispatch<SetStateAction<FieldState[]>>,
      disabled?: boolean
    ) =>
      type === "inner" ? (
        <ConfigInputTextComponent
          key={id}
          fieldId={id}
          control={control}
          setValue={setValue}
          enableValidate={enableValidate}
          disabled={disabled}
        />
      ) : (
        <PlainInputTextComponent
          key={id}
          fieldId={id}
          control={control}
          setValue={setValue}
          enableValidate={enableValidate}
          disabled={disabled}
          stateList={stateList}
          setStateList={setStateList}
        />
      ),
    propertyComponent: (id: string) => <InputTextPropertyForm fieldId={id} />,
    viewComponent: (state: FieldState) => <InputTextViewComponent state={state} />,
  },
  "drop-down": {
    fieldComponent: (
      type: string,
      id: string,
      control: Control,
      setValue: UseFormSetValue<any>,
      enableValidate: boolean,
      stateList: FieldState[],
      setStateList: Dispatch<SetStateAction<FieldState[]>>,
      disabled?: boolean
    ) =>
      type === "inner" ? (
        <ConfigDropDownComponent
          key={id}
          fieldId={id}
          control={control}
          setValue={setValue}
          enableValidate={enableValidate}
          disabled={disabled}
        />
      ) : (
        <PlainDropDownComponent
          key={id}
          fieldId={id}
          control={control}
          setValue={setValue}
          enableValidate={enableValidate}
          disabled={disabled}
          stateList={stateList}
          setStateList={setStateList}
        />
      ),
    propertyComponent: (id: string) => <DropDownPropertyForm fieldId={id} />,
    viewComponent: (state: FieldState) => <DropDownViewComponent state={state} />,
  },
  "date-picker": {
    fieldComponent: (
      type: string,
      id: string,
      control: Control,
      setValue: UseFormSetValue<any>,
      enableValidate: boolean,
      stateList: FieldState[],
      setStateList: Dispatch<SetStateAction<FieldState[]>>,
      disabled?: boolean
    ) =>
      type === "inner" ? (
        <ConfigDatePickerComponent
          key={id}
          fieldId={id}
          control={control}
          setValue={setValue}
          enableValidate={enableValidate}
          disabled={disabled}
        />
      ) : (
        <PlainDatePickerComponent
          key={id}
          fieldId={id}
          control={control}
          setValue={setValue}
          enableValidate={enableValidate}
          disabled={disabled}
          stateList={stateList}
          setStateList={setStateList}
        />
      ),
    propertyComponent: (id: string) => <DatePickerPropertyForm fieldId={id} />,
    viewComponent: (state: FieldState) => <DatePickerViewComponent state={state} />,
  },
  markdown: {
    fieldComponent: (
      type: string,
      id: string,
      control: Control,
      setValue: UseFormSetValue<any>,
      enableValidate: boolean,
      stateList: FieldState[],
      setStateList: Dispatch<SetStateAction<FieldState[]>>,
      disabled?: boolean
    ) => {
      return type === "inner" ? (
        <ConfigMarkdownComponent
          key={id}
          fieldId={id}
          control={control}
          setValue={setValue}
          enableValidate={enableValidate}
          disabled={disabled}
        />
      ) : (
        <PlainMarkdownComponent
          key={id}
          fieldId={id}
          control={control}
          setValue={setValue}
          enableValidate={enableValidate}
          disabled={disabled}
          stateList={stateList}
          setStateList={setStateList}
        />
      );
    },
    propertyComponent: (id: string) => <MarkdownPropertyForm fieldId={id} />,
    viewComponent: (state: FieldState) => <MarkdownViewComponent state={state} />,
  },
};

export const getComponent = (fieldType: string) => {
  return ComponentHelper[fieldType as keyof typeof ComponentHelper];
};

export const genUid = (prefix: string) => {
  return `${prefix}_${Math.random().toString().slice(2)}`;
};

export const getFieldSubType = (state: FieldState | undefined) => {
  if (state?.isMultiple) {
    return "multiple";
  }
  const fieldSubType = state?.fieldSubType ?? "";
  if (fieldSubType === "" || fieldSubType === "single") {
    return "single";
  }
  return fieldSubType;
};

export const translateRules = (
  rules:
    | {
        required?: FieldRuleDef | undefined;
        maxLength?: FieldRuleDef | undefined;
      }
    | undefined,
  enableValidate: boolean,
  visible: boolean
) => {
  const r = {
    required:
      enableValidate && rules?.required?.value === "y"
        ? {
            value: true,
            message: rules.required.message,
          }
        : undefined,
    maxLength:
      rules?.maxLength?.value !== ""
        ? {
            value: Number(rules?.maxLength?.value ?? 0),
            message: `The max character limit is ${rules?.maxLength?.value}.`,
          }
        : undefined,
  };

  return visible ? r : undefined;
};

export const StarOptions: FieldOptionDef[] = [
  {
    optionId: "option_start1",
    optionValue: "1",
  },
  {
    optionId: "option_start2",
    optionValue: "2",
  },
  {
    optionId: "option_start3",
    optionValue: "3",
  },
  {
    optionId: "option_start4",
    optionValue: "4",
  },
  {
    optionId: "option_start5",
    optionValue: "5",
  },
];
